import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, BadgeIndianRupee, HandCoins, Lock, Wallet } from 'lucide-react';
import {
  PAYMENT_METHODS,
  advanceAdjustmentPaise,
  parseRupees,
  payoutNetPaise,
  type PayoutAdjustmentKind,
  type PaymentMethod,
} from '@tailoring-crm/shared';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  PageSkeleton,
  useToast,
  cn,
} from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import {
  useRecordAdjustment,
  useRecordAdvance,
  useSettlePayout,
  useStaffLedger,
} from '../lib/payouts/use-payouts.js';
import { formatCurrency, formatDate } from '../lib/format.js';

/**
 * One person's wages: what they have earned, what is still owed, and the screen that pays it.
 *
 * THE FIGURE IS BUILT IN FRONT OF THE OWNER. Ticking a job moves the total; the additions, the
 * deductions and the advance being recovered are each a line with its reason beside it, and the
 * amount at the bottom is the sum of exactly what is on screen. It is computed with the SAME
 * shared function the server settles with, and sent back as `expectedNetPaise` — so if anything
 * moved between loading and pressing the button, the payment is refused rather than quietly
 * being for a different amount.
 *
 * NOTHING IS PAID BY DEFAULT AND EVERYTHING IS VISIBLE. Unpriced jobs are shown and cannot be
 * ticked: "nothing on the rate card prices this" is a thing the owner has to fix, not a row that
 * silently disappears.
 */
export function StaffPayoutPage(): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { staffId = '' } = useParams<{ staffId: string }>();
  const { role } = useAuth();

  const canSettle = role === 'owner' || role === 'manager';
  const canAdjust = role === 'owner';

  const ledger = useStaffLedger(canSettle ? staffId : null);
  const settle = useSettlePayout(staffId);

  const [selected, setSelected] = useState<string[]>([]);
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [applyAdvances, setApplyAdvances] = useState(true);
  const [dialog, setDialog] = useState<'advance' | 'adjustment' | null>(null);

  const payable = useMemo(
    () => (ledger.data?.unsettled ?? []).filter((job) => job.amountPaise !== null),
    [ledger.data],
  );
  /** A stable key for "the payable jobs changed", so the default selection follows the data. */
  const payableKey = payable.map((job) => job.assignmentId).join(',');

  useEffect(() => {
    setSelected(payableKey === '' ? [] : payableKey.split(','));
  }, [payableKey]);

  if (!canSettle) {
    return (
      <>
        <PageHeader title={t('payouts.staffTitle')} />
        <EmptyState
          icon={Lock}
          title={t('payouts.noAccessTitle')}
          description={t('payouts.noAccessHint')}
          action={
            <Button asChild>
              <Link to="/my-earnings">{t('payouts.goToMyEarnings')}</Link>
            </Button>
          }
        />
      </>
    );
  }

  if (ledger.isPending) return <PageSkeleton label={t('common.loading')} />;
  if (!ledger.data) {
    return (
      <EmptyState icon={Wallet} title={t('staff.notFound')} description={t('staff.notFoundHint')} />
    );
  }

  const data = ledger.data;
  const additionsPaise = data.pendingEntries
    .filter((entry) => entry.kind === 'addition')
    .reduce((sum, entry) => sum + entry.amountPaise, 0);
  const deductionsPaise = data.pendingEntries
    .filter((entry) => entry.kind === 'deduction')
    .reduce((sum, entry) => sum + entry.amountPaise, 0);

  const jobsPaise = payable
    .filter((job) => selected.includes(job.assignmentId))
    .reduce((sum, job) => sum + (job.amountPaise ?? 0), 0);

  const beforeAdvancePaise = jobsPaise + additionsPaise - deductionsPaise;
  const advanceAdjustedPaise = applyAdvances
    ? advanceAdjustmentPaise(data.summary.advanceOutstandingPaise, Math.max(0, beforeAdvancePaise))
    : 0;
  const netPaise = payoutNetPaise({
    jobsPaise,
    additionsPaise,
    deductionsPaise,
    advanceAdjustedPaise,
  });

  const toggle = (assignmentId: string): void => {
    setSelected((current) =>
      current.includes(assignmentId)
        ? current.filter((one) => one !== assignmentId)
        : [...current, assignmentId],
    );
  };

  const pay = async (): Promise<void> => {
    try {
      const payout = await settle.mutateAsync({
        assignmentIds: selected,
        method,
        paidAt,
        ...(note.trim() ? { note: note.trim() } : {}),
        applyAdvances,
        // The figure the owner is looking at. The server refuses anything else.
        expectedNetPaise: netPaise,
      });
      setNote('');
      toast({
        title: t('payouts.settled', {
          amount: formatCurrency(payout.netPaise),
          name: data.staffName,
        }),
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: error instanceof Error ? error.message : t('error.unexpected'),
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <div className="pb-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/payouts">
            <ArrowLeft aria-hidden="true" />
            {t('payouts.backToOverview')}
          </Link>
        </Button>
      </div>

      <PageHeader
        title={data.staffName}
        description={t('payouts.staffDescription')}
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setDialog('advance')}>
              <HandCoins aria-hidden="true" />
              {t('payouts.recordAdvance')}
            </Button>
            {canAdjust ? (
              <Button variant="outline" onClick={() => setDialog('adjustment')}>
                <BadgeIndianRupee aria-hidden="true" />
                {t('payouts.addAdjustment')}
              </Button>
            ) : null}
          </div>
        }
      />

      <div className="flex flex-col gap-5">
        {/* Earned, paid, outstanding — three figures, never one derived on screen from another. */}
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          {(
            [
              ['earned', data.summary.earnedPaise],
              ['paid', data.summary.paidPaise],
              ['outstanding', data.summary.outstandingPaise],
            ] as const
          ).map(([key, value]) => (
            <div key={key} className="rounded-lg border border-border p-3">
              <dt className="text-sm text-muted-foreground">{t(`payouts.${key}`)}</dt>
              <dd className="text-xl font-semibold tabular-nums">{formatCurrency(value)}</dd>
            </div>
          ))}
        </dl>

        {data.summary.advanceOutstandingPaise > 0 ? (
          <p role="status" className="rounded-lg border border-warning/50 bg-warning/5 p-3 text-sm">
            {t('payouts.advanceRunning', {
              amount: formatCurrency(data.summary.advanceOutstandingPaise),
            })}
          </p>
        ) : null}

        {/* --- The jobs --------------------------------------------------------------- */}
        <section className="flex flex-col gap-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="text-sm font-semibold">{t('payouts.unsettledHeading')}</h2>
            {payable.length > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() =>
                  setSelected(
                    selected.length === payable.length
                      ? []
                      : payable.map((job) => job.assignmentId),
                  )
                }
              >
                {selected.length === payable.length
                  ? t('payouts.selectNone')
                  : t('payouts.selectAll')}
              </Button>
            ) : null}
          </div>

          {data.unsettled.length === 0 ? (
            <EmptyState
              icon={Wallet}
              title={t('payouts.nothingOwedTitle')}
              description={t('payouts.nothingOwedHint')}
            />
          ) : (
            <ul className="flex flex-col gap-2">
              {data.unsettled.map((job) => {
                const priced = job.amountPaise !== null;
                const checked = selected.includes(job.assignmentId);
                return (
                  <li key={job.assignmentId}>
                    <label
                      className={cn(
                        'flex cursor-pointer flex-wrap items-center justify-between gap-3 rounded-lg border p-3',
                        priced ? 'border-border hover:bg-accent' : 'border-dashed border-border',
                        checked ? 'bg-accent/60' : '',
                      )}
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        <input
                          type="checkbox"
                          className="size-4 shrink-0 accent-primary"
                          checked={checked}
                          disabled={!priced}
                          aria-label={t('payouts.selectJob', {
                            garment: job.garmentTypeLabel,
                            order: job.orderNumber,
                          })}
                          onChange={() => toggle(job.assignmentId)}
                        />
                        <span className="min-w-0">
                          <span className="block font-medium">
                            {job.quantity} × {job.garmentTypeLabel} · {t(`stage.${job.stage}`)}
                          </span>
                          <span className="block text-sm text-muted-foreground">
                            {job.orderNumber} · {job.customerName} ·{' '}
                            {t('payouts.finishedOn', { date: formatDate(job.completedAt) })}
                          </span>
                        </span>
                      </span>

                      <span className="text-right">
                        {priced ? (
                          <>
                            <span className="block font-semibold tabular-nums">
                              {formatCurrency(job.amountPaise ?? 0)}
                            </span>
                            <span className="block text-xs text-muted-foreground tabular-nums">
                              {t('payouts.rateEach', {
                                rate: formatCurrency(job.ratePaise ?? 0),
                              })}
                            </span>
                          </>
                        ) : (
                          <Badge variant="warning">{t('payouts.unpriced')}</Badge>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* --- What else is on the figure ------------------------------------------------ */}
        {data.pendingEntries.length > 0 ? (
          <section className="flex flex-col gap-2">
            <h2 className="text-sm font-semibold">{t('payouts.adjustmentsHeading')}</h2>
            <ul className="flex flex-col gap-2">
              {data.pendingEntries.map((entry) => (
                <li
                  key={entry.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
                >
                  <span className="min-w-0">
                    <span className="block font-medium">
                      {t(`payouts.kind.${entry.kind}`)} — {entry.reason}
                    </span>
                    <span className="block text-sm text-muted-foreground">
                      {formatDate(entry.occurredAt)}
                      {entry.recordedByName ? ` · ${entry.recordedByName}` : ''}
                    </span>
                  </span>
                  <span className="font-semibold tabular-nums">
                    {entry.kind === 'deduction' ? '−' : '+'}
                    {formatCurrency(entry.amountPaise)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {/* --- Paying ---------------------------------------------------------------------- */}
        <section className="flex flex-col gap-3 rounded-lg border border-border p-4">
          <h2 className="text-sm font-semibold">{t('payouts.payHeading')}</h2>

          <dl className="flex flex-col gap-1 text-sm">
            <div className="flex justify-between gap-3">
              <dt>{t('payouts.jobsSelected', { count: selected.length })}</dt>
              <dd className="tabular-nums">{formatCurrency(jobsPaise)}</dd>
            </div>
            {additionsPaise > 0 ? (
              <div className="flex justify-between gap-3">
                <dt>{t('payouts.additions')}</dt>
                <dd className="tabular-nums">+{formatCurrency(additionsPaise)}</dd>
              </div>
            ) : null}
            {deductionsPaise > 0 ? (
              <div className="flex justify-between gap-3">
                <dt>{t('payouts.deductions')}</dt>
                <dd className="tabular-nums">−{formatCurrency(deductionsPaise)}</dd>
              </div>
            ) : null}
            {advanceAdjustedPaise > 0 ? (
              <div className="flex justify-between gap-3">
                <dt>{t('payouts.advanceAdjusted')}</dt>
                <dd className="tabular-nums">−{formatCurrency(advanceAdjustedPaise)}</dd>
              </div>
            ) : null}
            <div className="flex justify-between gap-3 border-t border-border pt-2 text-base font-semibold">
              <dt>{t('payouts.willPay')}</dt>
              <dd className="tabular-nums">{formatCurrency(netPaise)}</dd>
            </div>
          </dl>

          {data.summary.advanceOutstandingPaise > 0 ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                className="size-4 accent-primary"
                checked={applyAdvances}
                onChange={(event) => setApplyAdvances(event.target.checked)}
              />
              {t('payouts.applyAdvances')}
            </label>
          ) : null}

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="payout-method">{t('payouts.method')}</Label>
              <select
                id="payout-method"
                value={method}
                onChange={(event) => setMethod(event.target.value as PaymentMethod)}
                className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
              >
                {PAYMENT_METHODS.map((one) => (
                  <option key={one} value={one}>
                    {t(`payments.methods.${one}`)}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="payout-date">{t('payouts.paidOn')}</Label>
              <Input
                id="payout-date"
                type="date"
                value={paidAt}
                onChange={(event) => setPaidAt(event.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="payout-note">{t('payouts.note')}</Label>
              <Input
                id="payout-note"
                value={note}
                placeholder={t('payouts.notePlaceholder')}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
          </div>

          <Button
            type="button"
            disabled={selected.length === 0 || settle.isPending}
            onClick={() => void pay()}
          >
            <Wallet aria-hidden="true" />
            {t('payouts.paySubmit', { amount: formatCurrency(netPaise) })}
          </Button>
        </section>

        {/* --- What has already been paid --------------------------------------------------- */}
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold">{t('payouts.historyHeading')}</h2>
          {data.payouts.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('payouts.historyEmpty')}</p>
          ) : (
            <ul className="flex flex-col gap-2">
              {data.payouts.map((payout) => (
                <li
                  key={payout.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border p-3"
                >
                  <span className="min-w-0">
                    <span className="block font-medium">{payout.number}</span>
                    <span className="block text-sm text-muted-foreground">
                      {formatDate(payout.paidAt)} · {t(`payments.methods.${payout.method}`)} ·{' '}
                      {t('payouts.jobCount', { count: payout.jobs.length })}
                    </span>
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="font-semibold tabular-nums">
                      {formatCurrency(payout.netPaise)}
                    </span>
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/payouts/${payout.id}`}>{t('payouts.viewStatement')}</Link>
                    </Button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {dialog === 'advance' ? (
        <AdvanceDialog
          staffUserId={staffId}
          staffName={data.staffName}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog === 'adjustment' ? (
        <AdjustmentDialog staffUserId={staffId} onClose={() => setDialog(null)} />
      ) : null}
    </>
  );
}

// --- Recording an advance ----------------------------------------------------------------------

function AdvanceDialog({
  staffUserId,
  staffName,
  onClose,
}: {
  staffUserId: string;
  staffName: string;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const record = useRecordAdvance(staffUserId);

  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [paidAt, setPaidAt] = useState(() => new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const amountPaise = safeRupees(amount);

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      await record.mutateAsync({
        amountPaise,
        method,
        paidAt,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      toast({
        title: t('payouts.advanceRecorded', {
          amount: formatCurrency(amountPaise),
          name: staffName,
        }),
        variant: 'success',
      });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('payouts.recordAdvance')}</DialogTitle>
          <DialogDescription>{t('payouts.advanceHint')}</DialogDescription>
        </DialogHeader>

        {error ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {error}
          </p>
        ) : null}

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="advance-amount">{t('payouts.amount')}</Label>
            <Input
              id="advance-amount"
              inputMode="decimal"
              value={amount}
              onChange={(event) => setAmount(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="advance-method">{t('payouts.method')}</Label>
            <select
              id="advance-method"
              value={method}
              onChange={(event) => setMethod(event.target.value as PaymentMethod)}
              className="h-11 rounded-md border border-input bg-background px-3 text-base sm:text-sm"
            >
              {PAYMENT_METHODS.map((one) => (
                <option key={one} value={one}>
                  {t(`payments.methods.${one}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="advance-date">{t('payouts.paidOn')}</Label>
            <Input
              id="advance-date"
              type="date"
              value={paidAt}
              onChange={(event) => setPaidAt(event.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="advance-note">{t('payouts.note')}</Label>
            <Input
              id="advance-note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button disabled={amountPaise <= 0 || record.isPending} onClick={() => void submit()}>
            {t('payouts.advanceSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// --- A bonus or a stoppage -----------------------------------------------------------------------

function AdjustmentDialog({
  staffUserId,
  onClose,
}: {
  staffUserId: string;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const record = useRecordAdjustment(staffUserId);

  const [kind, setKind] = useState<PayoutAdjustmentKind>('addition');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const amountPaise = safeRupees(amount);

  const submit = async (): Promise<void> => {
    setError(null);
    try {
      await record.mutateAsync({ kind, amountPaise, reason: reason.trim() });
      toast({ title: t('payouts.adjustmentRecorded'), variant: 'success' });
      onClose();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t('error.unexpected'));
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('payouts.addAdjustment')}</DialogTitle>
          <DialogDescription>{t('payouts.adjustmentHint')}</DialogDescription>
        </DialogHeader>

        {error ? (
          <p role="alert" className="text-sm font-medium text-destructive">
            {error}
          </p>
        ) : null}

        <fieldset className="flex gap-2">
          <legend className="mb-1 text-sm font-medium">{t('payouts.adjustmentKind')}</legend>
          {(['addition', 'deduction'] as const).map((one) => (
            <label
              key={one}
              className={cn(
                'flex flex-1 cursor-pointer items-center gap-2 rounded-lg border p-3 text-sm',
                kind === one ? 'border-primary bg-accent' : 'border-border hover:bg-accent',
              )}
            >
              <input
                type="radio"
                name="adjustment-kind"
                value={one}
                checked={kind === one}
                onChange={() => setKind(one)}
                className="size-4 accent-primary"
              />
              {t(`payouts.kind.${one}`)}
            </label>
          ))}
        </fieldset>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="adjustment-amount">{t('payouts.amount')}</Label>
          <Input
            id="adjustment-amount"
            inputMode="decimal"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
        </div>

        {/* Required, and said so on the label: the reason is what makes the line defensible. */}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="adjustment-reason">{t('payouts.reason')}</Label>
          <Input
            id="adjustment-reason"
            value={reason}
            placeholder={t('payouts.reasonPlaceholder')}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={amountPaise <= 0 || reason.trim().length < 3 || record.isPending}
            onClick={() => void submit()}
          >
            {t('payouts.adjustmentSubmit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Rupees typed at a counter, in paise. Anything unparseable is zero, which disables the button. */
function safeRupees(value: string): number {
  try {
    return value.trim() === '' ? 0 : parseRupees(value);
  } catch {
    return 0;
  }
}

export default StaffPayoutPage;
